import { z } from 'zod';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { scheduleSchema } from './schedule.js';
import { paginatedResponse } from './util-schema-types.js';

import {
  minimalSchoolSchema,
  minimalSchoolWithOrgSchema,
  schoolSchema,
  schoolStatusSchema,
  schoolWithOrgAndPlainScheduleSchema,
  schoolWithOrgSchema,
} from './school-shared.js';
import { studentYearNullableSchema } from './student.js';
export { SchoolStatus } from './school-shared.js';
export type {
  MinimalSchool,
  MinimalSchoolWithOrg,
  School,
  SchoolWithOrg,
  SchoolWithOrgAndPlainSchedule,
} from './school-shared.js';
export {
  minimalSchoolSchema,
  minimalSchoolWithOrgSchema,
  schoolSchema,
  schoolStatusSchema,
  schoolWithOrgAndPlainScheduleSchema,
  schoolWithOrgSchema,
};

export const schoolWithOrgAndScheduleSchema = schoolWithOrgSchema.extend({
  schedules: z.array(z.lazy(() => scheduleSchema)),
});
export type SchoolWithOrgAndSchedule = z.infer<typeof schoolWithOrgAndScheduleSchema>;

export const schoolWithOrgEdlinkIdentifiable = schoolWithOrgSchema.extend({
  edlinkId: z.string().nullable(),
});
export type SchoolWithOrgEdlinkIdentifiable = z.infer<typeof schoolWithOrgEdlinkIdentifiable>;

export const schoolWithOrgAndScheduleEdlinkIdentifiable = schoolWithOrgAndScheduleSchema.extend({
  edlinkId: z.string().nullable(),
});
export type SchoolWithOrgAndScheduleEdlinkIdentifiable = z.infer<typeof schoolWithOrgAndScheduleEdlinkIdentifiable>;

export const customSchoolStatusSchema = z.enum(['upcoming', 'active', 'ended']);
export const CustomSchoolStatus = customSchoolStatusSchema.enum;
export type CustomSchoolStatus = z.infer<typeof customSchoolStatusSchema>;
export const CustomSchoolStatusLabels: Record<CustomSchoolStatus, string> = {
  upcoming: 'Upcoming schools',
  active: 'Active schools',
  ended: 'Ended schools',
};

export const getSchoolsFilterSchema = z.object({
  query: z.string().optional(),
  orgIds: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  customStatus: z.array(customSchoolStatusSchema).optional(),
  schoolIds: z.array(z.string()).optional(),
});
export type GetSchoolsFilter = z.infer<typeof getSchoolsFilterSchema>;

export const getSchoolsSortableColumnSchema = z.enum(['nextSessionPlainDate', 'orgName']);
export const GetSchoolsSortableColumn = getSchoolsSortableColumnSchema.enum;
export type GetSchoolsSortableColumn = z.infer<typeof getSchoolsSortableColumnSchema>;

export const getSchoolsSortOrderSchema = sortOrderSchemaFrom(getSchoolsSortableColumnSchema);
export type GetSchoolsSortOrder = z.infer<typeof getSchoolsSortOrderSchema>;

export const getSchoolsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getSchoolsFilterSchema))
  .merge(sortingSchemaFrom(getSchoolsSortableColumnSchema));
export type GetSchoolsModifier = z.infer<typeof getSchoolsModifierSchema>;

export const getSchoolsResultsSchema = paginatedResponse.extend({
  schools: z.array(schoolWithOrgAndPlainScheduleSchema),
});

export type GetSchoolsResults = z.infer<typeof getSchoolsResultsSchema>;

export const getMinimalSchoolsResultsSchema = paginatedResponse.extend({
  schools: z.array(minimalSchoolWithOrgSchema),
});

export type GetMinimalSchoolsResults = z.infer<typeof getMinimalSchoolsResultsSchema>;

export const getMinimalSchoolsWithOrgResultsSchema = paginatedResponse.extend({
  schools: z.array(minimalSchoolWithOrgSchema),
});

export type GetMinimalSchoolsWithOrgResults = z.infer<typeof getMinimalSchoolsWithOrgResultsSchema>;

export const createSchoolOptionsSchema = z.object({
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema.optional(),
  totalSeats: z.number().min(0).default(0).optional(),
  email: z.string().email({ message: 'Invalid email format' }).optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  schoolAdminName: z.string().optional(),
  needSpanishFeedback: z.boolean().default(false).optional(),
  edlinkId: z.string().optional(),
  v1Id: z.string().optional(),
});

export type CreateSchoolOptions = z.infer<typeof createSchoolOptionsSchema>;

export const updateSchoolOptionsSchema = z.object({
  name: z.string().optional(),
  orgId: z.string().optional(),
  schoolStatus: schoolStatusSchema.optional(),
  edlinkId: z.string().nullable().optional(),
  canSendSchoolParentReminders: z.boolean().optional(),
});
export type UpdateSchoolOptions = z.infer<typeof updateSchoolOptionsSchema>;
